import React, { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTerminalExtStore } from '../../store/terminalExtStore';
import { canSplit, computeRects, leafOrder } from './paneTreeModel';
import { useTerminalPaneShortcuts } from './useTerminalPaneShortcuts';
import TerminalPane from './TerminalPane';
import PaneDivider from './PaneDivider';
import './TerminalSplit.css';
import { useLayoutSettle } from './terminalLayoutSettle';

// Before the grid is measured (first frame, jsdom), rects are computed on a
// nominal box and expressed in percent so the layout is right from frame 0.
const NOMINAL_W = 1000;
const NOMINAL_H = 600;

const pctStyle = (r) => ({
  left: `${(r.x / NOMINAL_W) * 100}%`,
  top: `${(r.y / NOMINAL_H) * 100}%`,
  width: `${(r.w / NOMINAL_W) * 100}%`,
  height: `${(r.h / NOMINAL_H) * 100}%`,
});
const pxStyle = (r) => ({ left: r.x, top: r.y, width: r.w, height: r.h });
const FULL = { left: 0, top: 0, width: '100%', height: '100%' };

/** Grid size via one ResizeObserver, rAF-throttled, mirrored into the store. */
function useGridSize(ref, tabId) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  const setGridSize = useTerminalExtStore((s) => s.setGridSize);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (!w || !h) return; // hidden (display:none) — keep the last size
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
      setGridSize(tabId, w, h);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [ref, tabId, setGridSize]);
  return size;
}

/** Announce exits to screen readers (status colour alone is not enough). */
function useExitAnnouncer(panes, t) {
  const [msg, setMsg] = useState('');
  const seen = useRef({});
  useEffect(() => {
    panes.forEach((p) => {
      const key = `${p.status}:${p.restartNonce}`;
      if (seen.current[p.id] !== key && (p.status === 'exited' || p.status === 'error')) {
        const name = p.title || p.label || 'Terminal';
        setMsg(
          p.exitCode != null
            ? t('terminalExt.exitedCode', '{{name}} exited (code {{code}})', { name, code: p.exitCode })
            : t('terminalExt.exitedNoCode', '{{name}} exited', { name })
        );
      }
      seen.current[p.id] = key;
    });
  }, [panes, t]);
  return msg;
}

/**
 * All panes of one tab as FLAT, absolutely positioned siblings keyed by pane
 * id, plus one divider per split. Splitting, closing, zooming, dragging or
 * applying a preset only changes each pane's `style` — React never unmounts
 * a pane because the layout changed, so no shell is killed by a layout
 * change. DOM order is creation order (not layout order), so a split never
 * even moves an existing pane's node.
 */
export default function TerminalPaneGrid({ tab, visible }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const allPanes = useTerminalExtStore((s) => s.panes);
  const setRatio = useTerminalExtStore((s) => s.setRatio);
  const resetRatio = useTerminalExtStore((s) => s.resetRatio);
  const size = useGridSize(ref, tab.id);
  const [dragging, setDragging] = useState(false);
  const { onKeyDownCapture, keyFilter } = useTerminalPaneShortcuts(tab.id);

  const order = useMemo(() => leafOrder(tab.layout), [tab.layout]);
  const inTab = useMemo(() => new Set(order), [order]);
  // Creation order: object key order of the panes map.
  const domOrder = useMemo(() => Object.keys(allPanes).filter((id) => inTab.has(id)), [allPanes, inTab]);
  const panes = useMemo(() => order.map((id) => allPanes[id]).filter(Boolean), [order, allPanes]);
  const measured = size.w > 0 && size.h > 0;
  const { panes: rects, dividers } = useMemo(
    () => computeRects(tab.layout, measured ? size.w : NOMINAL_W, measured ? size.h : NOMINAL_H),
    [tab.layout, measured, size.w, size.h]
  );
  const multi = order.length > 1;
  const zoomed = tab.zoomedPaneId && inTab.has(tab.zoomedPaneId) ? tab.zoomedPaneId : null;
  const announcement = useExitAnnouncer(panes, t);
  // A split / close / zoom / preset / finished drag: one more refit + PTY size
  // once the layout has stopped changing (see terminalLayoutSettle.js).
  useLayoutSettle([tab.layout, zoomed, multi, measured], visible);

  const splitState = (paneId) => {
    if (tab.locked) return { row: { ok: false, reason: 'locked' }, col: { ok: false, reason: 'locked' } };
    return {
      row: canSplit(tab.layout, paneId, 'row', size.w, size.h),
      col: canSplit(tab.layout, paneId, 'col', size.w, size.h),
    };
  };

  return (
    <div
      ref={ref}
      className={`term-grid${multi ? ' term-grid--multi' : ''}${dragging ? ' term-grid--dragging' : ''}${visible ? '' : ' term-ext-instance--hidden'}`}
      role="group"
      aria-label={t('terminalExt.panesGroup', 'Terminal panes')}
      onKeyDownCapture={onKeyDownCapture}
    >
      {domOrder.map((id) => {
        const pane = allPanes[id];
        const r = rects[id];
        let style = FULL;
        if (multi && r && !(zoomed === id)) style = measured ? pxStyle(r) : pctStyle(r);
        return (
          <TerminalPane
            key={id}
            pane={pane}
            tab={tab}
            style={style}
            visible={visible}
            focused={tab.focusedPaneId === id}
            multi={multi}
            zoomHidden={!!zoomed && zoomed !== id}
            split={splitState(id)}
            keyFilter={keyFilter}
          />
        );
      })}
      {multi &&
        !zoomed &&
        dividers.map((d) => (
          <PaneDivider
            key={d.id}
            d={d}
            style={measured ? pxStyle(d) : pctStyle(d)}
            onRatio={(r) => setRatio(tab.id, d.id, r)}
            onReset={() => resetRatio(tab.id, d.id)}
            onDragChange={setDragging}
          />
        ))}
      <div className="term-sr-only" aria-live="polite">
        {announcement}
      </div>
    </div>
  );
}
